import { useEffect } from "react";
import { SITE, abs, webPageSchema, breadcrumbSchema } from "@/data/seo";

function upsert(selector, create, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

function meta(key, content, attr = "name") {
  const selector = `meta[${attr}="${key}"]`;
  if (content == null || content === "") {
    document.head.querySelector(selector)?.remove();
    return;
  }
  upsert(selector, () => document.createElement("meta"), { [attr]: key, content });
}

export function useSeo({
  title,
  description = SITE.description,
  path = "/",
  image = SITE.image,
  type = "website",
  pageType = "WebPage",
  noindex = false,
  breadcrumbs,
  article,
  schema = [],
}) {
  const extraKey = JSON.stringify({ breadcrumbs, article, schema });
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE.name}` : SITE.title;
    const url = abs(path);
    const img = abs(image);

    document.title = fullTitle;
    meta("description", description);
    meta("robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");

    meta("og:type", type, "property");
    meta("og:site_name", SITE.name, "property");
    meta("og:locale", SITE.locale, "property");
    meta("og:title", fullTitle, "property");
    meta("og:description", description, "property");
    meta("og:url", url, "property");
    meta("og:image", img, "property");
    meta("og:image:alt", title || SITE.name, "property");

    meta("twitter:card", "summary_large_image");
    meta("twitter:site", SITE.twitter);
    meta("twitter:title", fullTitle);
    meta("twitter:description", description);
    meta("twitter:image", img);

    meta("article:published_time", article?.published, "property");
    meta("article:modified_time", article?.modified || article?.published, "property");
    meta("article:section", article?.section, "property");
    meta("article:author", article ? SITE.name : undefined, "property");

    const canonical = document.head.querySelector('link[rel="canonical"]');
    if (noindex) canonical?.remove();
    else upsert('link[rel="canonical"]', () => document.createElement("link"), { rel: "canonical", href: url });

    const graph = [webPageSchema({ path, title: fullTitle, description, type: pageType, image })];
    if (breadcrumbs?.length) graph.push(breadcrumbSchema([{ name: "Home", path: "/" }, ...breadcrumbs]));
    graph.push(...schema.flat().filter(Boolean));
    const ld = upsert('script[data-seo="page"]', () => document.createElement("script"), {
      type: "application/ld+json",
      "data-seo": "page",
    });
    ld.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, type, pageType, noindex, extraKey]);
}
